import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

export type ListFiltersContextValue<T> = {
  filters: T
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void
  clearFilters: () => void
  /** Há algum filtro diferente do valor inicial (vazio). */
  hasFilters: boolean
}

const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)

/**
 * Cria um contexto tipado para o estado dos filtros de uma listagem. Assim a barra de filtros,
 * o botão "Limpar filtros" e o estado vazio da tabela compartilham o mesmo estado.
 *
 * @example
 * // core/ListFiltersProvider.ts
 * export const { ListFiltersProvider, useListFilters } =
 *   createListFilters<{ nome: string; status?: IOption<boolean> }>({ nome: '' })
 */
export const createListFilters = <T extends object>(initialFilters: T) => {
  const Context = createContext<ListFiltersContextValue<T>>({
    filters: initialFilters,
    setFilter: () => {},
    clearFilters: () => {},
    hasFilters: false,
  })

  const ListFiltersProvider: FC<{ children?: ReactNode }> = ({ children }) => {
    const [filters, setFilters] = useState<T>(initialFilters)

    const setFilter = useCallback(
      <K extends keyof T>(key: K, value: T[K]) => setFilters((prev) => ({ ...prev, [key]: value })),
      []
    )
    const clearFilters = useCallback(() => setFilters(initialFilters), [])
    const hasFilters = useMemo(
      () => Object.values(filters).some((value) => !isEmptyValue(value)),
      [filters]
    )

    const value = useMemo(
      () => ({ filters, setFilter, clearFilters, hasFilters }),
      [filters, setFilter, clearFilters, hasFilters]
    )

    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  const useListFilters = () => useContext(Context)

  return { ListFiltersProvider, useListFilters }
}
